import * as React from "react"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"
import { Badge, Button } from "@nexus/ui"

import { useAuth } from "@/features/auth/hooks/useAuth"
import { usePermissions } from "@/features/auth/hooks/usePermissions"
import {
  invalidatePersonaOnboarding,
  normalizedPersonaOpportunitySetTenantId,
  personaOnboardingStatusQueryOptions,
  personaOpportunitySetKeys,
  personasQueryOptions,
  useGeneratePersonaOpportunitySet,
} from "@/lib/queries"
import { useOpportunityGenerationEnabled } from "@/features/persona-opportunities/hooks/useOpportunityGenerationEnabled"
import { buildGeneratePersonaOpportunitySetRequest } from "@/features/persona-opportunities/api/persona-opportunity-sets.api"
import type { PersonaOpportunitySetListPage } from "@/features/persona-opportunities/api/persona-opportunity-sets.api"
import { CreatePersonaSetDialog } from "@/features/persona-opportunities/components/sets/CreatePersonaSetDialog/CreatePersonaSetDialog"
import { usePersonaOpportunityPermissions } from "@/features/persona-opportunities/hooks/usePersonaOpportunityPermissions"
import {
  applyOptimisticCreateToList,
  createOptimisticPersonaSetRow,
  createPendingPersonaSetId,
} from "@/features/persona-opportunities/lib/persona-opportunity-set-generation-state"
import type { CreatePersonaSetSubmit } from "@/features/persona-opportunities/types/persona-opportunities.types"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useNavigate } from "@tanstack/react-router"
import { continuePersonaOnboarding, completePersonaOnboarding } from "../../api/personas.api"
import { usePersonaOnboardingStore } from "../../store/personaOnboarding.store"
import { personaMatchesSearch, personaValueTier, sortPersonas, togglePersonaTableSort, normalizePersonaSortKey } from "../../lib/persona-format"
import {
  PERSONA_BANNER_DISMISSED_STORAGE_KEY,
  PERSONA_VIEW_MODE_STORAGE_KEY,
} from "../../constants/personas.constants"
import { PersonasInfoBanner } from "../PersonasInfoBanner/PersonasInfoBanner"
import { PersonaGrid } from "../PersonaGrid/PersonaGrid"
import { PersonaTable, PersonaTableSkeleton } from "../PersonaTable/PersonaTable"
import { PersonaGridSkeleton } from "../PersonaGrid/PersonaGrid"
import { PERSONA_FILTER_ALL, type PersonaStatusFilter } from "../PersonaFilters/PersonaFilters"
import {
  PersonaLibraryToolbar,
  type PersonaLibraryViewMode,
} from "../PersonaLibraryToolbar/PersonaLibraryToolbar"
import {
  PersonasNewAvailableBanner,
  resolvePersonasBannerMode,
} from "../PersonasNewAvailableBanner/PersonasNewAvailableBanner"
import { PersonaDetailDrawer } from "../PersonaDetailDrawer/PersonaDetailDrawer"
import { PersonasOnboardingFlow } from "../PersonasOnboardingFlow/PersonasOnboardingFlow"
import { PersonasOnboardingAnalyzingView } from "../PersonasOnboardingAnalyzing/PersonasOnboardingAnalyzingView"
import { PersonasOnboardingIntroView } from "../PersonasOnboardingIntro/PersonasOnboardingIntroView"
import { PersonasOnboardingSummary } from "../PersonasOnboardingSummary/PersonasOnboardingSummary"
import { PersonasWaitingState } from "../PersonasWaitingState/PersonasWaitingState"
import type {
  Persona,
  PersonaOnboardingContinueSummary,
  PersonaSortDirection,
  PersonaSortKey,
  PersonaTableSortKey,
} from "../../types/personas.types"

const INFO_BANNER_DISMISSED_KEY = "nexus_personas_info_banner_dismissed"
const DEFAULT_SORT_KEY: PersonaSortKey = "shareOfSpend"
const DEFAULT_SORT_DIR: PersonaSortDirection = "desc"

function readViewMode(): PersonaLibraryViewMode {
  try {
    const raw = localStorage.getItem(PERSONA_VIEW_MODE_STORAGE_KEY)
    return raw === "cards" ? "cards" : "table"
  } catch {
    return "table"
  }
}

function readDismissedSuggestedIds(): Set<string> {
  try {
    const raw = sessionStorage.getItem(PERSONA_BANNER_DISMISSED_STORAGE_KEY)
    if (!raw) return new Set()
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return new Set()
    return new Set(parsed.filter((id): id is string => typeof id === "string"))
  } catch {
    return new Set()
  }
}

function writeDismissedSuggestedIds(ids: Set<string>) {
  try {
    sessionStorage.setItem(PERSONA_BANNER_DISMISSED_STORAGE_KEY, JSON.stringify([...ids]))
  } catch {
    /* private browsing */
  }
}

interface PersonasPageProps {
  initialSelectedPersonaId?: string
}

export function PersonasPage({ initialSelectedPersonaId }: PersonasPageProps) {
  const { t } = useTranslation()
  const activeTenantId = useAuth((s) => s.activeTenantId)
  const tenants = useAuth((s) => s.tenants)
  const organizationName = tenants.find((tenant) => tenant.id === activeTenantId)?.name ?? null
  const perms = usePermissions()
  const canReadOnboardingStatus = perms.PERSONAS_ONBOARDING_STATUS?.isOn === true
  const canSuggestions = perms.PERSONAS_ONBOARDING_SUGGESTIONS?.isOn === true
  const canContinue = perms.PERSONAS_ONBOARDING_CONTINUE?.isOn === true
  const noPermission = t("common.noPermission", "You don't have permission to do this")

  const onboardingStatusQuery = useQuery({
    ...personaOnboardingStatusQueryOptions(activeTenantId),
    enabled: !!activeTenantId && canReadOnboardingStatus,
  })

  const [includeRejected, setIncludeRejected] = React.useState(false)
  const [viewMode, setViewMode] = React.useState<PersonaLibraryViewMode>(readViewMode)

  const { data, isLoading, isError } = useQuery(
    personasQueryOptions(activeTenantId, { includeRejected }),
  )
  const personas = data?.personas ?? []
  const rejectedCount = data?.rejectedCount ?? 0

  const librarySpendTotal = React.useMemo(
    () => personas.filter((p) => p.status !== "REJECTED").reduce((sum, p) => sum + p.spend, 0),
    [personas],
  )

  const [search, setSearch] = React.useState("")
  const [sortKey, setSortKey] = React.useState<PersonaSortKey>(DEFAULT_SORT_KEY)
  const [sortDir, setSortDir] = React.useState<PersonaSortDirection>(DEFAULT_SORT_DIR)
  const [industry, setIndustry] = React.useState(PERSONA_FILTER_ALL)
  const [valueTier, setValueTier] = React.useState(PERSONA_FILTER_ALL)
  const [statusFilter, setStatusFilter] = React.useState<PersonaStatusFilter>(PERSONA_FILTER_ALL)

  const industryOptions = React.useMemo(() => {
    const seen = new Set<string>()
    for (const p of personas) if (p.industry) seen.add(p.industry)
    return [...seen].sort()
  }, [personas])

  const filteredPersonas = React.useMemo(() => {
    const filtered = personas.filter((p) => {
      if (!personaMatchesSearch(p, search)) return false
      if (industry !== PERSONA_FILTER_ALL && p.industry !== industry) return false
      if (statusFilter !== PERSONA_FILTER_ALL && p.status !== statusFilter) return false
      if (valueTier !== PERSONA_FILTER_ALL) {
        const sharePct = librarySpendTotal > 0 ? (p.spend / librarySpendTotal) * 100 : 0
        if (personaValueTier(sharePct) !== valueTier) return false
      }
      return true
    })
    return sortPersonas(filtered, sortKey, sortDir)
  }, [personas, search, industry, valueTier, statusFilter, sortKey, sortDir, librarySpendTotal])

  const handleSortChange = React.useCallback((value: PersonaSortKey) => {
    setSortKey(value)
    setSortDir(DEFAULT_SORT_DIR)
  }, [])

  const handleTableSort = React.useCallback(
    (key: PersonaTableSortKey) => {
      const next = togglePersonaTableSort(sortKey, sortDir, key)
      setSortKey(next.sortKey)
      setSortDir(next.sortDir)
    },
    [sortKey, sortDir],
  )

  const tableSortKey = React.useMemo(() => normalizePersonaSortKey(sortKey), [sortKey])

  const hasActiveFilters =
    search.trim() !== "" ||
    industry !== PERSONA_FILTER_ALL ||
    valueTier !== PERSONA_FILTER_ALL ||
    statusFilter !== PERSONA_FILTER_ALL

  const handleClearFilters = React.useCallback(() => {
    setSearch("")
    setIndustry(PERSONA_FILTER_ALL)
    setValueTier(PERSONA_FILTER_ALL)
    setStatusFilter(PERSONA_FILTER_ALL)
  }, [])

  const handleIncludeRejectedChange = React.useCallback((value: boolean) => {
    setIncludeRejected(value)
    if (value) {
      setSortKey("status")
      setSortDir("asc")
    }
  }, [])

  const handleViewModeChange = React.useCallback((mode: PersonaLibraryViewMode) => {
    setViewMode(mode)
    try {
      localStorage.setItem(PERSONA_VIEW_MODE_STORAGE_KEY, mode)
    } catch {
      /* private browsing */
    }
  }, [])

  const [selectedPersonaId, setSelectedPersonaId] = React.useState<string | null>(
    initialSelectedPersonaId ?? null,
  )

  React.useEffect(() => {
    setSelectedPersonaId(initialSelectedPersonaId ?? null)
  }, [initialSelectedPersonaId])

  const [infoBannerOpen, setInfoBannerOpen] = React.useState<boolean>(() => {
    try {
      return localStorage.getItem(INFO_BANNER_DISMISSED_KEY) !== "true"
    } catch {
      return true
    }
  })
  const handleDismissInfoBanner = React.useCallback(() => {
    setInfoBannerOpen(false)
    try {
      localStorage.setItem(INFO_BANNER_DISMISSED_KEY, "true")
    } catch {
      /* private browsing */
    }
  }, [])

  const [dismissedSuggestedIds, setDismissedSuggestedIds] = React.useState(readDismissedSuggestedIds)

  const [createSetPersona, setCreateSetPersona] = React.useState<Persona | null>(null)

  const handleCreateOpportunitySet = React.useCallback((persona: Persona) => {
    // The drawer is the other entry point into this dialog — close it rather than
    // stack a modal on top of a sheet.
    setSelectedPersonaId(null)
    setCreateSetPersona(persona)
  }, [])

  React.useEffect(() => {
    if (!isError) return
    toast.error(t("personas.toast.loadFailed", "Failed to load user personas"))
  }, [isError, t])

  const onboardingStatusLoading = canReadOnboardingStatus && onboardingStatusQuery.isLoading
  const onboardingStatus = onboardingStatusQuery.data

  const hasSuggestedPersonas =
    canReadOnboardingStatus &&
    onboardingStatusQuery.isSuccess &&
    (onboardingStatus?.suggested ?? 0) > 0

  const hasPersonas = personas.length > 0
  const hasLibraryPersonas = personas.some((p) => p.status === "ACTIVE")

  const [analyzeDone, setAnalyzeDone] = React.useState(false)
  const [introDone, setIntroDone] = React.useState(false)
  const [reviewStarted, setReviewStarted] = React.useState(false)
  const [summary, setSummary] = React.useState<PersonaOnboardingContinueSummary | null>(null)
  const [finishedSessionId, setFinishedSessionId] = React.useState<string | undefined>(undefined)
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { canCreateSet, canUseQueryTuning } = usePersonaOpportunityPermissions()
  const { isPersonaEnabled } = useOpportunityGenerationEnabled(activeTenantId)
  const canCreateFromPersona = canCreateSet && isPersonaEnabled
  const generatePersonaSet = useGeneratePersonaOpportunitySet()

  // Generation itself belongs to Persona Opportunities: this only starts the run and
  // hands it over, so the sets page owns the outcome exactly as it does for a run
  // started there. The optimistic row goes in on the 202 — a failure leaves the user
  // here with the dialog still open, so there is nothing to roll back.
  const handleGeneratePersonaSet = React.useCallback(
    (submit: CreatePersonaSetSubmit) => {
      if (!activeTenantId) return
      generatePersonaSet.mutate(
        buildGeneratePersonaOpportunitySetRequest(submit, {
          tenantId: activeTenantId,
          idempotencyKey: crypto.randomUUID(),
          canUseQueryTuning,
        }),
        {
          onSuccess: ({ correlationId, setId }) => {
            const placeholderId = setId ?? createPendingPersonaSetId()
            queryClient.setQueryData<PersonaOpportunitySetListPage>(
              personaOpportunitySetKeys.list(
                normalizedPersonaOpportunitySetTenantId(activeTenantId),
              ),
              (previous) =>
                applyOptimisticCreateToList(
                  previous,
                  createOptimisticPersonaSetRow(submit, activeTenantId, placeholderId),
                ),
            )
            setCreateSetPersona(null)
            void navigate({
              to: "/persona-opportunities",
              search: {
                tab: "opportunities",
                view: "table",
                status: "all",
                pendingSetId: placeholderId,
                pendingCorrelationId: correlationId,
              },
            })
          },
        },
      )
    },
    [activeTenantId, canUseQueryTuning, generatePersonaSet, navigate, queryClient],
  )

  // Custom create does not start generation. Open the new set on the Hub scripts
  // tab, the same destination the Hub uses after a custom set is created there.
  const handleUserGeneratedCreated = React.useCallback(
    (created: { id: string }) => {
      setCreateSetPersona(null)
      void navigate({
        to: "/persona-opportunities",
        search: {
          tab: "opportunities",
          view: "table",
          status: "all",
          setIds: created.id,
          hubTab: "scripts",
        },
      })
    },
    [navigate],
  )

  const startOnboardingSession = usePersonaOnboardingStore((s) => s.startSession)
  const mintOnboardingSession = usePersonaOnboardingStore((s) => s.mintSession)
  const clearOnboardingSession = usePersonaOnboardingStore((s) => s.clearSession)

  const resolveOnboardingSessionId = React.useCallback((): string | undefined => {
    if (!activeTenantId) return undefined
    return startOnboardingSession(activeTenantId)
  }, [activeTenantId, startOnboardingSession])

  const needsOnboarding =
    canReadOnboardingStatus &&
    onboardingStatusQuery.isSuccess &&
    ((onboardingStatus?.suggested ?? 0) > 0 || onboardingStatus?.awaitingFinish === true)

  const personasSyncInFlight =
    canReadOnboardingStatus &&
    onboardingStatusQuery.isSuccess &&
    onboardingStatus?.personasSyncInFlight === true

  const isEmptyDestination =
    !onboardingStatusLoading && !needsOnboarding && !isLoading && !hasLibraryPersonas

  // First-run = onboarding never finished and no prior decisions (no ACTIVE, no
  // unprocessed rejects). A tenant can finish onboarding leaving every persona
  // Undecided, which zeroes both counters — `hasCompletedOnboarding` is what stops
  // the intro from reappearing for them.
  const isFirstRun =
    canReadOnboardingStatus &&
    onboardingStatusQuery.isSuccess &&
    onboardingStatus?.hasCompletedOnboarding !== true &&
    (onboardingStatus?.active ?? 0) === 0 &&
    (onboardingStatus?.unprocessedRejected ?? 0) === 0

  React.useEffect(() => {
    setAnalyzeDone(false)
    setIntroDone(false)
    setReviewStarted(false)
    setSummary(null)
    setFinishedSessionId(undefined)
  }, [activeTenantId])

  React.useEffect(() => {
    if (!activeTenantId || !needsOnboarding) return
    startOnboardingSession(activeTenantId)
  }, [activeTenantId, needsOnboarding, startOnboardingSession])

  const handleAnalyzeReady = React.useCallback(() => {
    setAnalyzeDone(true)
  }, [])

  const handleIntroStart = React.useCallback(() => {
    resolveOnboardingSessionId()
    setIntroDone(true)
  }, [resolveOnboardingSessionId])

  const handleStartReview = React.useCallback(() => {
    if (activeTenantId) mintOnboardingSession(activeTenantId)
    setReviewStarted(true)
  }, [activeTenantId, mintOnboardingSession])

  const handleExitReview = React.useCallback(() => {
    setReviewStarted(false)
  }, [])

  const handleOnboardingFinished = React.useCallback(
    (next: PersonaOnboardingContinueSummary, onboardingSessionId?: string) => {
      setReviewStarted(false)
      setSummary(next)
      setFinishedSessionId(onboardingSessionId ?? resolveOnboardingSessionId())
    },
    [resolveOnboardingSessionId],
  )

  const completeMut = useMutation({
    mutationFn: completePersonaOnboarding,
    onSuccess: async () => {
      setSummary(null)
      setFinishedSessionId(undefined)
      if (activeTenantId) clearOnboardingSession(activeTenantId)
      if (!activeTenantId) return
      await invalidatePersonaOnboarding(queryClient, activeTenantId)
    },
    onError: () => {
      toast.error(t("personas.toast.completeFailed", "Couldn't start persona recalculation. Please try again."))
    },
  })

  const completingRef = React.useRef(false)

  const handleGoToLibrary = React.useCallback(() => {
    if (completingRef.current || completeMut.isPending) return
    completingRef.current = true
    completeMut.mutate(
      { onboardingSessionId: finishedSessionId ?? resolveOnboardingSessionId() },
      { onSettled: () => { completingRef.current = false } },
    )
  }, [completeMut, finishedSessionId, resolveOnboardingSessionId])

  const finishReadyMut = useMutation({
    mutationFn: continuePersonaOnboarding,
    onSuccess: async (data, vars) => {
      handleOnboardingFinished(
        data.summary,
        vars?.onboardingSessionId ?? resolveOnboardingSessionId(),
      )
      if (!activeTenantId) return
      await invalidatePersonaOnboarding(queryClient, activeTenantId)
    },
    onError: () => {
      toast.error(t("personas.toast.finishFailed", "Couldn't finish setup. Please try again."))
    },
  })

  const suggestedIds = React.useMemo(
    () => personas.filter((p) => p.status === "SUGGESTED").map((p) => p.id),
    [personas],
  )

  const suggestedCount = onboardingStatus?.suggested ?? 0
  const awaitingFinish = onboardingStatus?.awaitingFinish === true
  const hasCompletedOnboarding = onboardingStatus?.hasCompletedOnboarding === true

  const bannerMode = resolvePersonasBannerMode({
    suggestedCount,
    hasCompletedOnboarding,
    awaitingFinish,
  })

  const hasUndismissedSuggested = suggestedIds.some((id) => !dismissedSuggestedIds.has(id))
  // An owed Finish overrides dismissal in every mode, not just `finish` — New is a
  // permanent state now, so a tenant with skipped personas never leaves `review`.
  const bannerOpen =
    awaitingFinish ||
    (bannerMode !== null && (suggestedIds.length === 0 || hasUndismissedSuggested))
  const isBannerVisible = bannerMode !== null && bannerOpen

  const handleDismissNewBanner = React.useCallback(() => {
    const next = new Set(dismissedSuggestedIds)
    for (const id of suggestedIds) next.add(id)
    setDismissedSuggestedIds(next)
    writeDismissedSuggestedIds(next)
  }, [dismissedSuggestedIds, suggestedIds])

  if (summary) {
    return (
      <PersonasOnboardingSummary
        summary={summary}
        newCount={onboardingStatus?.suggested ?? 0}
        onGoToLibrary={handleGoToLibrary}
        isPublishing={completeMut.isPending}
      />
    )
  }

  if (personasSyncInFlight && !reviewStarted && (needsOnboarding || !hasLibraryPersonas)) {
    return (
      <PersonasOnboardingAnalyzingView
        isDataReady={false}
        organizationName={organizationName}
        onReady={handleAnalyzeReady}
      />
    )
  }

  if (needsOnboarding && hasSuggestedPersonas && isFirstRun && !introDone) {
    return (
      <PersonasOnboardingIntroView
        onStart={handleIntroStart}
        canStart={canSuggestions}
        canLoadSuggestions={canSuggestions}
        organizationName={organizationName}
      />
    )
  }

  if (isEmptyDestination && !analyzeDone) {
    return (
      <PersonasOnboardingAnalyzingView
        isDataReady={isEmptyDestination}
        organizationName={organizationName}
        onReady={handleAnalyzeReady}
      />
    )
  }

  if (reviewStarted && hasSuggestedPersonas) {
    return (
      <PersonasOnboardingFlow onFinished={handleOnboardingFinished} onExit={handleExitReview} />
    )
  }

  // The banner owns the primary CTA whenever it is on screen; the toolbar entry is the
  // standing fallback for after it has been dismissed. Gating on `suggestedCount` alone
  // rendered both at once, because AD-2518 turned New into a permanent resting state and
  // the banner stopped being a transient announcement.
  // An owed Finish never reaches the toolbar: it forces `bannerOpen` and is undismissable,
  // so the banner is always the one showing it.
  const reviewAction =
    suggestedCount > 0 && !isBannerVisible
      ? {
          kind: "review" as const,
          onClick: handleStartReview,
          disabled: !canSuggestions,
          disabledReason: !canSuggestions ? noPermission : undefined,
        }
      : undefined

  const showAllRejectedEmpty =
    !includeRejected &&
    !isLoading &&
    !hasPersonas &&
    rejectedCount > 0

  const bodySkeleton =
    viewMode === "table" ? <PersonaTableSkeleton /> : <PersonaGridSkeleton />

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-xl font-semibold text-foreground">
              {t("personas.title", "User Personas")}
            </h1>
            <Badge variant="ai" iconStart="sparkles">
              {t("personas.usedAcross", "Used across Opportunities, Briefs & Scripts")}
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground">
            {t(
              "personas.description",
              "Generate and manage audience personas that Adge uses to create more relevant opportunities, briefs, and scripts.",
            )}
          </p>
        </div>
      </div>

      <PersonasInfoBanner open={infoBannerOpen} onClose={handleDismissInfoBanner} />

      {bannerMode !== null && (
        <PersonasNewAvailableBanner
          open={bannerOpen}
          suggestedCount={suggestedCount}
          hasCompletedOnboarding={hasCompletedOnboarding}
          awaitingFinish={awaitingFinish}
          onDismiss={handleDismissNewBanner}
          onSetup={handleStartReview}
          onReview={handleStartReview}
          onFinish={() =>
            finishReadyMut.mutate({ onboardingSessionId: resolveOnboardingSessionId() })
          }
          canStart={canSuggestions}
          canFinish={canContinue}
          isFinishing={finishReadyMut.isPending}
        />
      )}

      <PersonaLibraryToolbar
        search={search}
        onSearchChange={setSearch}
        sortKey={sortKey}
        onSortChange={handleSortChange}
        showSort={viewMode === "cards"}
        industry={industry}
        onIndustryChange={setIndustry}
        industryOptions={industryOptions}
        valueTier={valueTier}
        onValueTierChange={setValueTier}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        hasActiveFilters={hasActiveFilters}
        onClearAll={handleClearFilters}
        viewMode={viewMode}
        onViewModeChange={handleViewModeChange}
        includeRejected={includeRejected}
        onIncludeRejectedChange={handleIncludeRejectedChange}
        reviewAction={reviewAction}
      />

      {isLoading && !data ? (
        bodySkeleton
      ) : showAllRejectedEmpty ? (
        <div
          data-testid="personas-all-rejected-empty"
          className="flex flex-col items-center gap-3 rounded-md border border-dashed border-border py-12 text-center"
        >
          <p className="text-sm font-medium text-foreground">
            {t("personas.waiting.allRejectedTitle", "Every persona was rejected")}
          </p>
          <p className="max-w-md text-sm text-muted-foreground">
            {t(
              "personas.waiting.allRejectedDescription",
              "Turn on Show rejected to see them in your library.",
            )}
          </p>
          <Button variant="outline" size="sm" onClick={() => handleIncludeRejectedChange(true)}>
            {t("personas.toolbar.showRejected", "Show rejected")}
          </Button>
        </div>
      ) : !hasPersonas ? (
        <PersonasWaitingState variant="body" />
      ) : filteredPersonas.length > 0 ? (
        viewMode === "table" ? (
          <PersonaTable
            personas={filteredPersonas}
            librarySpendTotal={librarySpendTotal}
            sortKey={tableSortKey}
            sortDir={sortDir}
            onSort={handleTableSort}
            onPersonaClick={(id) => setSelectedPersonaId(id)}
            onCreateOpportunitySet={canCreateFromPersona ? handleCreateOpportunitySet : undefined}
          />
        ) : (
          <PersonaGrid
            personas={filteredPersonas}
            totalSpend={librarySpendTotal}
            onCreateOpportunitySet={canCreateFromPersona ? handleCreateOpportunitySet : undefined}
            onOpenDetail={(persona) => {
              if (persona.status !== "REJECTED") setSelectedPersonaId(persona.id)
            }}
          />
        )
      ) : (
        <div
          data-testid="personas-empty-filtered"
          className="flex flex-col items-center gap-2 rounded-md border border-dashed border-border py-12 text-center"
        >
          <p className="text-sm text-muted-foreground">
            {t("personas.filters.noMatches", "No user personas match your filters.")}
          </p>
          <Button variant="outline" size="sm" onClick={handleClearFilters}>
            {t("common.clearFilters", "Clear filters")}
          </Button>
        </div>
      )}

      <PersonaDetailDrawer
        personaId={selectedPersonaId}
        totalSpend={librarySpendTotal}
        onOpenChange={(open) => {
          if (!open) setSelectedPersonaId(null)
        }}
        onCreateOpportunitySet={canCreateFromPersona ? handleCreateOpportunitySet : undefined}
      />

      {canCreateFromPersona && activeTenantId && (
        <CreatePersonaSetDialog
          tenantId={activeTenantId}
          open={createSetPersona !== null}
          onOpenChange={(open) => {
            if (!open) setCreateSetPersona(null)
          }}
          initialPersonaId={createSetPersona?.id}
          isSubmitting={generatePersonaSet.isPending}
          canUseQueryTuning={canUseQueryTuning}
          onSubmit={handleGeneratePersonaSet}
          onUserGeneratedCreated={handleUserGeneratedCreated}
        />
      )}
    </div>
  )
}
